import { withErrorBoundary } from '@navi-app/utils';
import { Theme } from 'baseui';
import { Tag as BaseTag, TagOverrides } from 'baseui/tag';
import { ReactNode } from 'react';
import TextEllipsis from './text-ellipsis';

export type TTagHierarchy = 'primary' | 'secondary' | 'tertiary';

export type TTagColor =
  | 'gray'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'purple'
  | 'magenta';

export type TTagSize = 'xs' | 'sm' | 'md' | 'lg';

export type TTagKind =
  | 'primary'
  | 'accent'
  | 'accent-solid'
  | 'positive'
  | 'warning'
  | 'light-warning'
  | 'negative'
  | 'dark'
  | 'secondary'
  | 'light-secondary';

type TStyleProps = { $theme: Theme };

type TStyleObject = Record<string, string | number>;

type TTag = {
  content: string | ReactNode;
  kind?: TTagKind;
  hierarchy?: TTagHierarchy;
  color?: TTagColor;
  size?: TTagSize;
  selected?: boolean;
  disabled?: boolean;
  customClass?: string;
  overrides?: TagOverrides;
  ellipsisLength?: number;
  hideEllipsis?: boolean;
  onClick?: () => void;
};

const KIND_ALIAS: Record<
  TTagKind,
  { hierarchy: TTagHierarchy; color: TTagColor }
> = {
  primary: { hierarchy: 'secondary', color: 'gray' },
  dark: { hierarchy: 'primary', color: 'gray' },
  accent: { hierarchy: 'secondary', color: 'blue' },
  'accent-solid': { hierarchy: 'primary', color: 'blue' },
  positive: { hierarchy: 'secondary', color: 'green' },
  warning: { hierarchy: 'secondary', color: 'yellow' },
  'light-warning': { hierarchy: 'primary', color: 'yellow' },
  negative: { hierarchy: 'secondary', color: 'red' },
  secondary: { hierarchy: 'primary', color: 'purple' },
  'light-secondary': { hierarchy: 'secondary', color: 'purple' },
};

const BASE_SIZE: Record<TTagSize, 'xSmall' | 'small' | 'medium' | 'large'> = {
  xs: 'xSmall',
  sm: 'small',
  md: 'medium',
  lg: 'large',
};

const BORDER_WIDTH: Record<TTagSize, string> = {
  xs: '1px',
  sm: '1px',
  md: '1px',
  lg: '2px',
};

const TERTIARY_HEIGHT: Record<TTagSize, string> = {
  xs: '16px',
  sm: '16px',
  md: '20px',
  lg: '24px',
};

const CONTENT_GAP: Record<TTagSize, string> = {
  xs: '4px',
  sm: '8px',
  md: '8px',
  lg: '12px',
};

const SELECTED_BORDER: Record<TTagColor, string> = {
  gray: '#727272',
  blue: '#1E64DC',
  green: '#0E8345',
  yellow: '#D79900',
  orange: '#C54600',
  red: '#DE1135',
  purple: '#944DE7',
  magenta: '#CA26A5',
};

const TERTIARY_CONTENT = '#03222F';

const NO_MARGIN = {
  marginTop: '0px',
  marginRight: '0px',
  marginBottom: '0px',
  marginLeft: '0px',
};

const capitalize = (value: string) =>
  `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

const themeColor = ($theme: Theme, name: string) =>
  ($theme.colors as unknown as Record<string, string>)[name];

const resolveStyle = (
  style: unknown,
  props: TStyleProps
): TStyleObject | undefined => {
  if (typeof style === 'function') {
    return (style as (a: TStyleProps) => TStyleObject)(props);
  }
  return style as TStyleObject | undefined;
};

const mergeSlot = (
  slotOverride: unknown,
  baseStyle: (props: TStyleProps) => TStyleObject
) => ({
  ...(slotOverride as object),
  style: (props: TStyleProps) => ({
    ...baseStyle(props),
    ...resolveStyle((slotOverride as { style?: unknown })?.style, props),
  }),
});

const TagComponent = ({
  content,
  kind,
  hierarchy,
  color,
  size = 'sm',
  selected,
  disabled,
  customClass,
  overrides,
  ellipsisLength,
  hideEllipsis,
  onClick,
}: TTag) => {
  const alias = KIND_ALIAS[kind ?? 'primary'];
  const resolvedHierarchy = hierarchy ?? alias.hierarchy;
  const resolvedColor = color ?? alias.color;
  const isTertiary = resolvedHierarchy === 'tertiary';
  const token = capitalize(resolvedColor);

  const tertiaryStyle = ({ $theme }: TStyleProps): TStyleObject => ({
    ...NO_MARGIN,
    backgroundColor: 'transparent',
    borderTopWidth: '0px',
    borderRightWidth: '0px',
    borderBottomWidth: '0px',
    borderLeftWidth: '0px',
    color: disabled
      ? themeColor($theme, `tag${token}ContentStateDisabled`)
      : TERTIARY_CONTENT,
    height: TERTIARY_HEIGHT[size],
    paddingTop: '0px',
    paddingRight: '0px',
    paddingBottom: '0px',
    paddingLeft: '0px',
  });

  const borderedStyle = ({ $theme }: TStyleProps): TStyleObject => {
    const borderColor = disabled
      ? themeColor($theme, `tag${token}BackgroundStateDisabled`)
      : (selected && SELECTED_BORDER[resolvedColor]) ||
        themeColor(
          $theme,
          `tag${token}Border${capitalize(resolvedHierarchy)}Unselected`
        );
    const borderWidth = selected && !disabled ? '2px' : BORDER_WIDTH[size];

    return {
      ...NO_MARGIN,
      borderTopColor: borderColor,
      borderRightColor: borderColor,
      borderBottomColor: borderColor,
      borderLeftColor: borderColor,
      borderTopWidth: borderWidth,
      borderRightWidth: borderWidth,
      borderBottomWidth: borderWidth,
      borderLeftWidth: borderWidth,
    };
  };

  const contentStyle = (): TStyleObject => ({
    alignItems: 'center',
    columnGap: CONTENT_GAP[size],
    display: 'inline-flex',
    flexWrap: 'nowrap',
  });

  const {
    Root: rootOverride,
    Text: textOverride,
    ...restOverrides
  } = overrides ?? {};

  const tag = (
    <BaseTag
      closeable={false}
      disabled={disabled}
      kind={resolvedColor}
      hierarchy={isTertiary ? 'secondary' : resolvedHierarchy}
      size={BASE_SIZE[size]}
      contentMaxWidth={null}
      onClick={onClick ? () => onClick() : undefined}
      overrides={{
        ...restOverrides,
        Root: mergeSlot(rootOverride, (props) =>
          isTertiary ? tertiaryStyle(props) : borderedStyle(props)
        ),
        Text: mergeSlot(textOverride, contentStyle),
      }}
    >
      {content}
    </BaseTag>
  );

  return hideEllipsis ? (
    <div className={customClass}>{tag}</div>
  ) : (
    <div className={customClass}>
      <TextEllipsis
        line={1}
        propsTooltip={{ content, minLengthText: ellipsisLength }}
      >
        {tag}
      </TextEllipsis>
    </div>
  );
};

export default withErrorBoundary(TagComponent);
